import AiLabel from "@/components/ai-label";
import Category from "@/components/category";
import TeaserDownpull from "@/components/teaser-downpull";
import TeaserHeading from "@/components/teaser-heading";
import TeaserLinkedContent from "@/components/teaser-linked-content";
import { Content } from "@/services/content/types";
import { getEstimatedReadingTime } from "@/utils/getEstimatedReadingTime";
import { createImageSizes, createImageUrl } from "@/utils/image";
import classNames from "classnames";
import Image from "next/image";
import Link from "next/link";
import Vinjett from "../../vinjett";

type MediumProps = {
  specialByline?: React.ReactNode;
  article: Content.Article;
  component?: string;
  showLinkedContent?: boolean;
  fullWidth?: boolean;
  hideDownPull?: boolean;
};

const Medium = ({
  article,
  component = "",
  showLinkedContent = true,
  fullWidth = false,
  hideDownPull = false,
  specialByline,
}: MediumProps) => {
  const category = article.fields.categories?.[0];
  const image = article.fields.teaserImage || article.fields.images?.[0];
  const title = article.fields.teaserTitle || article.fields.title;
  const excerpt = article.fields.excerpt || "";
  const author = article.fields.authors?.[0];
  const hasLinkedContent = Boolean(article.fields.linkedContent?.length);
  const readTime = getEstimatedReadingTime(article.fields.content);
  const vinjett = article.fields.vinjett;
  const teaserLayoutType = article.fields.teaserLayoutType;

  return (
    <div className="bg-sand-600 h-full">
      <Link
        href={`/${article.fields.slug}`}
        className={classNames("group block relative overflow-hidden", {
          "w-screen md:w-auto -ml-4 md:ml-0": fullWidth,
          "": teaserLayoutType === "Centrerad rubrik" && hasLinkedContent,
        })}
      >
        {vinjett && <Vinjett content={vinjett} />}
        {image?.fields?.file?.url && (
          <div className="aspect-w-16 aspect-h-9">
            <Image
              fill
              src={createImageUrl(image.fields.file.url, { crop: "16:9" })}
              alt={image.fields.alt || ""}
              className="object-cover"
              sizes={createImageSizes({
                mobile: "100vw",
                tablet: "50vw",
                desktop: "33vw",
              })}
            />
            <AiLabel image={image} />
          </div>
        )}
        <div
          className={classNames("p-4 bg-sand-600", {
            "pb-0": hasLinkedContent,
          })}
        >
          <div className="flex flex-col">
            {category && (
              <Category
                category={category}
                centered={teaserLayoutType === "Centrerad rubrik"}
              />
            )}
          </div>
          {title && (
            <TeaserHeading
              text={title}
              className={classNames(
                "text-xl md:text-2xl font-normal",
                teaserLayoutType === "Centrerad rubrik" ? "text-center" : "",
              )}
            />
          )}
          {!hideDownPull && excerpt && (
            <div className="mt-2">
              <TeaserDownpull
                authorName={author?.fields.name}
                excerpt={excerpt}
                centered={teaserLayoutType === "Centrerad rubrik"}
              />
            </div>
          )}
          {showLinkedContent && hasLinkedContent && (
            <div className="bg-sand-800 h-[1px] mt-2" />
          )}
        </div>
      </Link>
      {showLinkedContent && hasLinkedContent && (
        <div
          className={classNames(
            "border-b border-b-sand-800 bg-sand-600 -mt-4 last:border-0",
            {
              "w-screen md:w-auto -ml-4 md:ml-0 p-4": fullWidth,
            },
          )}
        >
          <TeaserLinkedContent
            linkedContent={article.fields.linkedContent}
            showLinkedContent={hasLinkedContent}
            compactMode={true}
            fontWeight="font-normal"
          />
        </div>
      )}
      {specialByline}
    </div>
  );
};

export default Medium;
